import React from 'react';
import { Link } from 'react-router-dom';
import styles from './LearnMore.module.css';
import whychooseus1 from '../assets/images/whychooseus-1.png';
import whychooseus2 from '../assets/images/whychooseus-2.png';
import whychooseus3 from '../assets/images/whychooseus-3.png';
import heroBackground from '../assets/images/about-1.png';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaCheckCircle, FaUserMd, FaShieldAlt, FaHeart, FaClock, FaHome, FaChevronDown, FaCertificate, FaClipboardCheck, FaGlobe, FaLock, FaFileInvoiceDollar, FaAward, FaUserNurse, FaHandHoldingMedical, FaLanguage } from 'react-icons/fa';
import SEO from '../components/SEO';

const LearnMore = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'What services does All For You Home Health Care provide?',
      answer: 'We offer a comprehensive range of home health care services including skilled nursing, physical therapy, occupational therapy, speech therapy, social services, certified home health aide services, Alzheimer\'s care, geriatric care management, medication reminders, and post-hospitalization care.'
    },
    {
      question: 'How do I get started with home health care services?',
      answer: 'Getting started is easy! Simply contact us for a free consultation. We\'ll discuss your needs, conduct a comprehensive assessment, create a personalized care plan, and match you with qualified caregivers. Our team will guide you through every step of the process.'
    },
    {
      question: 'Are your caregivers licensed and insured?',
      answer: 'Yes, all our caregivers are fully licensed, certified, bonded, and insured. Our team consists of experienced healthcare professionals including registered nurses, licensed therapists, and certified home health aides who undergo thorough background checks and continuous training.'
    },
    {
      question: 'What areas do you serve?',
      answer: 'We proudly serve five counties throughout Southern California: Los Angeles County, Orange County, Riverside County, San Bernardino County, and Ventura County. Contact us to confirm if we provide services in your specific area.'
    },
    {
      question: 'Does insurance cover home health care services?',
      answer: 'Many insurance plans, including Medicare and Medicaid, cover home health care services when medically necessary. Our team will verify your benefits and explain your options during the consultation.'
    },
    {
      question: 'Can I choose my caregiver?',
      answer: 'Yes! We carefully match caregivers to clients based on needs, preferences, and personality. If for any reason you\'re not satisfied with your caregiver match, we\'ll work with you to find a better fit. Your comfort and satisfaction are our top priorities.'
    },
    {
      question: 'What is the difference between skilled nursing and home health aide services?',
      answer: 'Skilled nursing involves medical care provided by licensed nurses, including wound care, medication administration, and IV therapy. Home health aides provide non-medical support with daily activities like bathing, dressing, meal preparation, and companionship. Both services can be combined based on your needs.'
    },
    {
      question: 'How often will my caregiver visit?',
      answer: 'Visit frequency is customized to your specific needs and care plan. Services can range from a few hours per week to regular daily visits. We offer flexible scheduling including weekends and holidays to ensure you receive the support you need when you need it.'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Initial Consultation',
      description: 'Contact us for a free consultation. We\'ll discuss your needs, answer questions, and explain our services in detail.'
    },
    {
      number: '02',
      title: 'Assessment & Planning',
      description: 'Our team conducts a comprehensive assessment and creates a personalized care plan tailored to your specific requirements.'
    },
    {
      number: '03',
      title: 'Care Begins',
      description: 'We match you with qualified caregivers and begin providing compassionate, professional care in your home.'
    },
    {
      number: '04',
      title: 'Ongoing Support',
      description: 'We continuously monitor and adjust your care plan, ensuring the highest quality service and your complete satisfaction.'
    }
  ];

  const whyChooseUs = [
    {
      icon: <FaUserMd />,
      title: 'Experienced Professionals',
      description: 'Our team consists of licensed, certified healthcare professionals with years of experience in home health care.',
      image: whychooseus1
    },
    {
      icon: <FaShieldAlt />,
      title: 'Fully Licensed & Insured',
      description: 'We are fully licensed, bonded, and insured, ensuring peace of mind for you and your family.',
      image: whychooseus2
    },
    {
      icon: <FaHeart />,
      title: 'Compassionate Care',
      description: 'We treat every client with dignity, respect, and compassion, as if they were our own family members.',
      image: whychooseus3
    }
  ];

  const benefits = [
    {
      icon: <FaHome />,
      title: 'Comfort of Home',
      description: 'Receive quality care in the familiar, comfortable environment of your own home.'
    },
    {
      icon: <FaClock />,
      title: 'Flexible Scheduling',
      description: 'We work around your schedule with services available 24/7, including weekends and holidays.'
    },
    {
      icon: <FaCheckCircle />,
      title: 'Personalized Care Plans',
      description: 'Every care plan is customized to meet your unique needs, preferences, and health goals.'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Quality Assurance',
      description: 'Regular supervision and quality checks ensure you receive the highest standard of care.'
    }
  ];

  const credentials = [
    {
      icon: <FaCertificate />,
      title: 'Licensed Agency',
      description: 'Licensed Home Health Care Agency in California'
    },
    {
      icon: <FaAward />,
      title: 'ACHC Accredited',
      description: 'Accreditation Commission for Health Care certified'
    },
    {
      icon: <FaUserNurse />,
      title: 'Licensed Staff',
      description: 'Registered & Licensed Nursing Staff and Certified Home Health Aides'
    },
    {
      icon: <FaHandHoldingMedical />,
      title: 'Therapy Services',
      description: 'Physical, Occupational & Speech Therapy Services'
    },
    {
      icon: <FaFileInvoiceDollar />,
      title: 'Insurance Accepted',
      description: 'Medicare & Insurance Billing Supported'
    },
    {
      icon: <FaLock />,
      title: 'HIPAA Compliant',
      description: 'Full compliance with patient privacy regulations'
    },
    {
      icon: <FaLanguage />,
      title: 'Multilingual Team',
      description: 'English, Armenian, Russian, Spanish, Persian'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Bonded & Insured',
      description: 'Fully bonded and insured for your protection'
    }
  ];

  return (
    <div className={styles.learnMorePage}>
      <SEO
        title="Learn More"
        description="Learn more about All For You Home Health Care services, benefits, process, and FAQs. Get answers to common questions about home health care in Southern California."
        keywords="learn more, home health care guide, how it works, benefits, FAQ, getting started, home care process"
        canonicalUrl="https://allforyouhc.com/learn-more"
      />
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroContainer}>
          <p className={styles.heroTag}>LEARN MORE</p>
          <h1 className={styles.heroTitle}>
            Your Partner in<br />Quality Home Health Care
          </h1>
          <p className={styles.heroDescription}>
            Discover how All For You Home Health Care can help you or your loved ones
            maintain independence, dignity, and quality of life with professional in-home care services.
          </p>
          <div className={styles.heroButtons}>
            <Link to="/contact" className={styles.heroButtonPrimary}>
              Get Started Today
            </Link>
            <Link to="/services" className={styles.heroButtonSecondary}>
              Browse Services
            </Link>
          </div>
          <div className={styles.heroCredentials}>
            {credentials.map((credential, index) => (
              <div key={index} className={styles.heroCredentialItem}>
                <div className={styles.heroCredentialIcon}>{credential.icon}</div>
                <span className={styles.heroCredentialText}>{credential.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      {/* <section className={styles.credentialsSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Certifications & Credentials</h2>
            <p className={styles.sectionDescription}>
              Our commitment to excellence is reflected in our comprehensive licensing,
              accreditations, and professional certifications.
            </p>
          </div>
          <div className={styles.credentialsGrid}>
            {credentials.map((credential, index) => (
              <div key={index} className={styles.credentialCard}>
                <div className={styles.credentialIcon}>{credential.icon}</div>
                <h3 className={styles.credentialTitle}>{credential.title}</h3>
                <p className={styles.credentialDescription}>{credential.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* How It Works Section */}
      <section className={styles.howItWorksSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>How It Works</h2>
            <p className={styles.sectionDescription}>
              Getting started with our home health care services is simple and straightforward.
              We've designed our process to be as easy and stress-free as possible.
            </p>
          </div>
          <div className={styles.stepsGrid}>
            {steps.map((step, index) => (
              <div key={index} className={styles.stepCard}>
                <div className={styles.stepNumber}>{step.number}</div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepDescription}>{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className={styles.whyChooseSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Why Choose Us</h2>
            <p className={styles.sectionDescription}>
              We're committed to providing exceptional home health care services that make
              a real difference in the lives of our clients and their families.
            </p>
          </div>
          <div className={styles.whyChooseGrid}>
            {whyChooseUs.map((item, index) => (
              <div key={index} className={styles.whyChooseCard}>
                <div className={styles.whyChooseImage}>
                  <img src={item.image} alt={item.title} />
                </div>
                <div className={styles.whyChooseContent}>
                  <div className={styles.whyChooseIcon}>{item.icon}</div>
                  <h3 className={styles.whyChooseTitle}>{item.title}</h3>
                  <p className={styles.whyChooseDescription}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsContent}>
            <div className={styles.benefitsText}>
              <h2 className={styles.sectionTitle}>Benefits of Home Health Care</h2>
              <p className={styles.benefitsDescription}>
                Home health care offers numerous advantages over traditional facility-based care,
                allowing patients to maintain their independence while receiving professional
                medical support in a comfortable, familiar environment.
              </p>
            </div>
            <div className={styles.benefitsGrid}>
              {benefits.map((benefit, index) => (
                <div key={index} className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>{benefit.icon}</div>
                  <h3 className={styles.benefitTitle}>{benefit.title}</h3>
                  <p className={styles.benefitDescription}>{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
            <p className={styles.sectionDescription}>
              Find answers to common questions about our home health care services.
              If you don't see your question here, feel free to contact us.
            </p>
          </div>
          <div className={styles.faqContainer}>
            {faqs.map((faq, index) => (
              <div key={index} className={styles.faqItem}>
                <button
                  className={`${styles.faqQuestion} ${openFaq === index ? styles.active : ''}`}
                  onClick={() => toggleFaq(index)}
                >
                  <span>{faq.question}</span>
                  <FaChevronDown className={`${styles.faqIcon} ${openFaq === index ? styles.rotate : ''}`} />
                </button>
                <div className={`${styles.faqAnswer} ${openFaq === index ? styles.open : ''}`}>
                  <div className={styles.faqAnswerContent}>
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>Ready to Get Started?</h2>
            <p className={styles.ctaText}>
              Contact us today to learn more about our services and how we can help you or your
              loved one receive quality care at home. Our team is here to answer your questions
              and guide you through the process.
            </p>
            <div className={styles.ctaButtons}>
              <Link to="/contact" className={styles.ctaButtonPrimary}>
                Contact Us
              </Link>
              <a href="tel:8189491031" className={styles.ctaButtonSecondary}>
                <FaPhone /> Call Us Now
              </a>
            </div>
            {/* <div className={styles.contactInfo}>
              <div className={styles.contactItem}>
                <FaPhone />
                <span>(818) 949-1031</span>
              </div>
              <div className={styles.contactItem}>
                <FaEnvelope />
                <span>allforyouhhc@gmail.com</span>
              </div>
              <div className={styles.contactItem}>
                <FaMapMarkerAlt />
                <span>40 N. Altadena Dr. Ste 200J, Pasadena, CA 91107</span>
              </div>
            </div> */}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LearnMore;
